import React from "react";

// Reactive `prefers-reduced-motion` hook. Returns `true` when the user has
// asked the OS to minimize motion, `false` otherwise. Updates live when the
// setting changes mid-session.
//
// SSR-safe: during server rendering (no `window`) it returns `false`, so the
// client's initial hydrated HTML doesn't mismatch. The actual preference is
// read on first effect and on subsequent MediaQueryList `change` events.
//
// Shared across any component that used to branch on `window.matchMedia`
// inline (HeroBanner auto-advance, WebinarCarousel cover rotation,
// useFadeInOnEnter, and the new useHideOnScrollDown hook).
export default function useReducedMotion() {
  // Lazy initializer — on CSR first render we read the real preference
  // synchronously so consumers don't have to wait a frame for the effect
  // to correct a false positive. On SSR there's no window, so we return
  // false (assume motion is OK); the effect will reconcile on mount.
  const [reduced, setReduced] = React.useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    // Re-read in case the value flipped between initial render and effect.
    setReduced(mq.matches);

    const onChange = (event) => setReduced(event.matches);
    // `addEventListener` is the modern API; `addListener` is the pre-2020
    // fallback Safari still shipped for a while. Guard both.
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  return reduced;
}
